import type { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { notReadyQuestion, notReadyText } from "./cutover";
import { countsText, fieldsText } from "./dataSource";
import { dateLabel, readingTime } from "./format";
import { confirmText, counted, switchChoices, switchState, type SwitchMode, WRITE_COLUMNS } from "./salesAreas";
import { saveFailure } from "./save";
import { CODES } from "./sheetImport";
import { Skeleton } from "./Skeleton";

const issueLabels = Object.fromEntries(Object.entries(CODES).map(([code, found]) => [code, found.text]));

/** Design 26.11 F4: the date, the preview and the confirm of a switch, a cancel or a switch back. */
export function AreaSwitchPanel({
  user,
  area,
  mode,
  timeZone,
  onSaved,
  onClose,
}: {
  user: User;
  area: Pick<api.SalesArea, "id" | "code" | "pickup_source" | "app_from" | "switch_state"> & Partial<Pick<api.CutoverArea, "readiness">>;
  timeZone?: string;
  mode: SwitchMode;
  onSaved: (next: api.SalesAreas, to: api.PickupSource) => void;
  onClose: () => void;
}) {
  const state = switchState(area);
  const to: api.PickupSource = mode === "schedule" ? "app" : "sheet";
  const [day, setDay] = useState(state === "scheduled" && area.app_from ? area.app_from : "");
  const [preview, setPreview] = useState<api.SwitchPreview | "failed" | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const wanted = to === "app" ? day : "";

  useEffect(() => {
    heading.current?.focus();
  }, []);
  useEffect(() => {
    let live = true;
    setPreview(undefined);
    user
      .getIdToken()
      .then((token) => api.switchPreview(token, area.id, to, wanted || undefined))
      .then(
        (found) => live && setPreview(found),
        () => live && setPreview("failed"),
      );
    return () => {
      live = false;
    };
  }, [user, area.id, to, wanted]);

  const found = typeof preview === "object" ? preview : null;
  const chosen = day || found?.app_from || "";
  const tomorrow = found?.choices.tomorrow;
  const warning = mode === "schedule" ? notReadyText({ code: area.code, readiness: area.readiness ?? null }) : null;
  const confirm = async () => {
    if (warning && !window.confirm(notReadyQuestion(area.code))) return;
    setSaving(true);
    setProblem(null);
    try {
      onSaved(await api.setAreaPickupSource(await user.getIdToken(), area.id, to, to === "app" ? chosen || undefined : undefined), to);
    } catch (e) {
      setProblem(saveFailure(e, "The switch").text);
    } finally {
      setSaving(false);
    }
  };
  const title =
    mode === "cancel" ? `Cancel the switch of area ${area.code}` : mode === "back" ? `Switch area ${area.code} back to the sheet` : `Switch area ${area.code} to the app`;
  return (
    <section className="area-switch" data-testid="area-switch" data-mode={mode} data-area={area.code} aria-busy={preview === undefined}>
      <h2 ref={heading} tabIndex={-1}>
        {title}
      </h2>
      {warning && (
        <p className="error" data-testid="area-switch-not-ready">
          {warning}
        </p>
      )}
      {mode === "schedule" && (
        <div className="actions" role="group" aria-label="The switch date">
          {found &&
            switchChoices(found.choices).map((choice) => (
              <button
                key={choice.key}
                type="button"
                className={chosen === choice.day ? "primary" : undefined}
                aria-pressed={chosen === choice.day}
                data-testid={`area-switch-${choice.key}`}
                onClick={() => setDay(choice.day)}
              >
                {choice.label} ({dateLabel(choice.day)})
              </button>
            ))}{" "}
          <label>
            Date{" "}
            <input type="date" data-testid="area-switch-date" min={tomorrow} value={chosen} onChange={(e) => e.target.value && setDay(e.target.value)} />
          </label>
        </div>
      )}
      {preview === undefined && <Skeleton rows={2} label="Loading the preview" />}
      {preview === "failed" && <p className="error">Cannot load the preview. You can still confirm, or try again later.</p>}
      {found && <PreviewText preview={found} mode={mode} day={chosen} timeZone={timeZone} />}
      {problem && (
        <p role="alert" className="error" data-testid="area-switch-problem">
          {problem}
        </p>
      )}
      <p className="actions">
        <button type="button" className={warning ? undefined : "primary"} data-testid="area-switch-confirm" disabled={saving || (to === "app" && !chosen && preview !== "failed")} onClick={() => void confirm()}>
          {saving ? "Saving…" : confirmText(mode, state, chosen ? dateLabel(chosen) : "tomorrow")}
        </button>{" "}
        <button type="button" data-testid="area-switch-close" onClick={onClose}>
          Close
        </button>
      </p>
    </section>
  );
}

function PreviewText({ preview, mode, day, timeZone }: { preview: api.SwitchPreview; mode: SwitchMode; day: string; timeZone?: string }) {
  const accounts = counted(preview.accounts, "account");
  const run = preview.import_run ? ` (from the import run of ${readingTime(preview.import_run.at, timeZone)})` : "";
  if (mode === "cancel") {
    return (
      <p data-testid="area-switch-preview">
        The {accounts} of the area stay on the sheet. The import keeps writing them, and their sheet pickups keep counting.
      </p>
    );
  }
  if (mode === "back") {
    const writes = preview.writes ?? 0;
    return (
      <div data-testid="area-switch-preview">
        <p>
          Dry run{run}: the import writes {counted(writes, "value")} of the {accounts} again
          {writes ? `: ${countsText(preview.by_column, WRITE_COLUMNS)}` : ""}. It adds {counted(preview.new_accounts ?? 0, "new account")}.
        </p>
        <p>
          {counted(preview.sheet_pickups_count_again ?? 0, "sheet pickup")} from {preview.app_from ? dateLabel(preview.app_from) : "the switch date"} count again, except a week that an app pickup replaces. The app
          pickups stay and count.
        </p>
        {Boolean(preview.write_rows?.length) && (
          <details>
            <summary>Show the values</summary>
            <ul data-testid="area-switch-writes">
              {preview.write_rows?.map((row, index) => (
                <li key={`${row.number}-${row.column}-${index}`}>
                  {row.number}: {WRITE_COLUMNS[row.column] ?? row.column} {row.value ? `→ ${row.value}` : ""}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  }
  return (
    <div data-testid="area-switch-preview">
      <p>
        From {day ? dateLabel(day) : "the switch date"}, the app owns the {accounts} of the area. The import does not write these fields: {fieldsText(preview.frozen_fields)}. A change in the sheet becomes an
        import issue.
      </p>
      <p>
        The import still writes the sheet pickups, but they do not count: {counted(preview.sheet_pickups_from ?? 0, "sheet pickup")} from that date now, and{" "}
        {counted(preview.sheet_pickups_cycle ?? 0, "sheet pickup")} in the last 4 weeks.
      </p>
      <p>
        Open import issues{run}: {countsText(preview.issues, issueLabels)}.
      </p>
      {preview.rows.length > 0 && (
        <details>
          <summary>Show the accounts</summary>
          <ul data-testid="area-switch-accounts">
            {preview.rows.map((row) => (
              <li key={row.account_id}>
                {row.number} {row.name}
              </li>
            ))}
          </ul>
          {preview.accounts > preview.rows.length && <p className="sub">The first {preview.rows.length} of {preview.accounts}.</p>}
        </details>
      )}
    </div>
  );
}
